import { useState, useMemo, memo, useEffect, useCallback, Fragment } from "react";
import { useChat, type Channel } from "../hooks/useChat";
import { useSidebarLayout, type SidebarCategory } from "../hooks/useSidebarLayout";
import { Avatar } from "./Avatar";
import { NewChatDialog } from "./NewChatDialog";
import {
  PenSquare, Settings, LogOut, User, Users, ShieldCheck,
  PanelLeftClose, PanelLeftOpen, ChevronDown, ChevronRight,
  MoreVertical, Plus, GripVertical,
} from "lucide-react";
import { safePreviewText } from "../lib/safeMessageText";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  useDroppable,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import styles from "./Sidebar.module.css";
import { UsageBar } from "./UsageBar";
import { useOrgUsage } from "../hooks/useOrgUsage";
import { zeigeVerbrauch } from "../lib/usage-org";

const COLLAPSED_KEY = "kollegeplus:sidebar-collapsed";
const UNCATEGORIZED_ID = "__uncategorized__";
// Category headers are draggable to reorder. They live in a separate id namespace
// so their sortable ids never collide with the per-category droppable bucket ids
// (which equal the bare category id and are channel-drop targets).
const CATEGORY_DRAG_PREFIX = "cat::";

/**
 * Resolve the category a dragged CHANNEL was dropped into. Exported for tests.
 *
 * KOL-100: a drop on the category HEADER arrives with the header's sortable id
 * (`cat::<id>`), not the bucket id — before this branch existed such drops fell
 * through to `null` and the channel was silently persisted as uncategorized.
 */
export function resolveDropCategoryId(
  overId: string,
  categoryIds: string[],
  assignments: Array<{ channelId: string; categoryId: string }>,
): string | null {
  if (overId === UNCATEGORIZED_ID) return null;
  if (overId.startsWith(CATEGORY_DRAG_PREFIX)) {
    const id = overId.slice(CATEGORY_DRAG_PREFIX.length);
    return categoryIds.includes(id) ? id : null;
  }
  if (categoryIds.includes(overId)) return overId;
  return assignments.find((a) => a.channelId === overId)?.categoryId ?? null;
}

interface SidebarProps {
  user: { id: string; displayName: string; avatarUrl?: string | null };
  onLogout: () => void;
  onSettings?: () => void;
  onAdmin?: () => void;
  isAdmin?: boolean;
}

export function Sidebar({ user, onLogout, onSettings, onAdmin, isAdmin }: SidebarProps) {
  const { channels, activeChannelId, setActiveChannel, isLoadingChannels, onlineUsers } = useChat();
  const {
    categories, assignments, createCategory, renameCategory,
    deleteCategory, toggleExpanded, syncLayout,
  } = useSidebarLayout();

  // KOL-94: Der Balken zeigt den Verbrauch der Organisation des geöffneten
  // Kanals. Ohne Kanalbezug nur bei genau einer Organisation — summieren wäre
  // falsch, es sind getrennte Verträge.
  const activeChannelOrgId = channels.find((c) => c.id === activeChannelId)?.orgId ?? null;
  const orgUsage = useOrgUsage(activeChannelOrgId);

  const [searchQuery, setSearchQuery] = useState("");
  const [showNewChat, setShowNewChat] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(() => {
    try { return localStorage.getItem(COLLAPSED_KEY) === "1"; } catch { return false; }
  });
  const [menuForCategory, setMenuForCategory] = useState<{ id: string; x: number; y: number } | null>(null);
  const [renamingCategoryId, setRenamingCategoryId] = useState<string | null>(null);
  const [renameDraft, setRenameDraft] = useState("");

  useEffect(() => {
    try { localStorage.setItem(COLLAPSED_KEY, isCollapsed ? "1" : "0"); } catch { /* ignore */ }
  }, [isCollapsed]);

  // Close context menu on outside click
  useEffect(() => {
    if (!menuForCategory) return;
    const close = () => setMenuForCategory(null);
    window.addEventListener("click", close);
    return () => window.removeEventListener("click", close);
  }, [menuForCategory]);

  const filteredChannels = useMemo(() => {
    return channels.filter((ch) => {
      if (!searchQuery) return true;
      let name = ch.name ?? "Direktnachricht";
      if (ch.type === "dm" && ch.members) {
        const partner = ch.members.find((m) => m.userId !== user.id);
        if (partner?.displayName) name = partner.displayName;
      }
      return name.toLowerCase().includes(searchQuery.toLowerCase());
    });
  }, [channels, searchQuery, user.id]);

  // Bucket channels by assignment. Uncategorized: time-sort. Categorized: sort_order-sort.
  const { uncategorizedChannels, channelsByCategory } = useMemo(() => {
    const assignmentByChannel = new Map(assignments.map((a) => [a.channelId, a]));
    const uncategorized: Channel[] = [];
    const byCategory = new Map<string, Channel[]>();

    for (const ch of filteredChannels) {
      const a = assignmentByChannel.get(ch.id);
      if (a && categories.some((c) => c.id === a.categoryId)) {
        const list = byCategory.get(a.categoryId) ?? [];
        list.push(ch);
        byCategory.set(a.categoryId, list);
      } else {
        uncategorized.push(ch);
      }
    }

    // Uncategorized: newest message first (existing behavior)
    uncategorized.sort((a, b) => {
      const aTime = a.lastMessage?.createdAt ?? "";
      const bTime = b.lastMessage?.createdAt ?? "";
      if (!aTime && !bTime) return 0;
      if (!aTime) return 1;
      if (!bTime) return -1;
      return bTime.localeCompare(aTime);
    });

    // Categorized: follow sort_order from assignments
    for (const list of byCategory.values()) {
      list.sort((a, b) => {
        const aSort = assignmentByChannel.get(a.id)?.sortOrder ?? 0;
        const bSort = assignmentByChannel.get(b.id)?.sortOrder ?? 0;
        return aSort - bSort;
      });
    }

    return { uncategorizedChannels: uncategorized, channelsByCategory: byCategory };
  }, [filteredChannels, assignments, categories]);

  // ── Drag & Drop ──
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    // ── Category header reorder ──
    // Active id in the "cat::" namespace = a category being dragged. Resolve the
    // target category from whatever it was dropped on (another header, a category
    // bucket, or a channel inside one) and persist the new category order. All
    // existing channel assignments are re-emitted unchanged.
    if (String(active.id).startsWith(CATEGORY_DRAG_PREFIX)) {
      const fromCatId = String(active.id).slice(CATEGORY_DRAG_PREFIX.length);
      const rawOver = String(over.id);
      let toCatId: string | null = null;
      if (rawOver.startsWith(CATEGORY_DRAG_PREFIX)) {
        toCatId = rawOver.slice(CATEGORY_DRAG_PREFIX.length);
      } else if (categories.some((c) => c.id === rawOver)) {
        toCatId = rawOver;
      } else {
        toCatId = assignments.find((a) => a.channelId === rawOver)?.categoryId ?? null;
      }
      if (!toCatId || toCatId === fromCatId) return;
      const oldIndex = categories.findIndex((c) => c.id === fromCatId);
      const newIndex = categories.findIndex((c) => c.id === toCatId);
      if (oldIndex < 0 || newIndex < 0) return;

      const reordered = arrayMove(categories, oldIndex, newIndex);
      const keepAssignments: Array<{ channelId: string; categoryId: string | null; sortOrder: number }> = [];
      for (const cat of categories) {
        (channelsByCategory.get(cat.id) ?? []).forEach((c, i) => {
          keepAssignments.push({ channelId: c.id, categoryId: cat.id, sortOrder: i });
        });
      }
      void syncLayout(reordered.map((c, i) => ({ id: c.id, sortOrder: i })), keepAssignments);
      return;
    }

    const activeChannelId = String(active.id);
    const overId = String(over.id);

    // Resolve target bucket from overId (bucket-id, category header, or another channel-id)
    const targetCategoryId = resolveDropCategoryId(
      overId,
      categories.map((c) => c.id),
      assignments,
    );

    // Build the list of channels in the target bucket, after the drop.
    const activeChannelAssignment = assignments.find((a) => a.channelId === activeChannelId);
    const sourceCategoryId = activeChannelAssignment ? activeChannelAssignment.categoryId : null;

    const targetList = targetCategoryId === null
      ? [...uncategorizedChannels]
      : [...(channelsByCategory.get(targetCategoryId) ?? [])];

    // Remove from target if it was already there
    const removeIdx = targetList.findIndex((c) => c.id === activeChannelId);
    if (removeIdx >= 0) targetList.splice(removeIdx, 1);

    // Compute insertion index
    let insertIdx = targetList.length;
    if (overId !== UNCATEGORIZED_ID && !categories.some((c) => c.id === overId)) {
      const overIdx = targetList.findIndex((c) => c.id === overId);
      if (overIdx >= 0) insertIdx = overIdx;
    }

    // Get active channel and insert
    const activeChannel = filteredChannels.find((c) => c.id === activeChannelId);
    if (!activeChannel) return;
    targetList.splice(insertIdx, 0, activeChannel);

    // Rebuild full assignments set
    const nextAssignments: Array<{ channelId: string; categoryId: string | null; sortOrder: number }> = [];

    // Source bucket (if different from target) — renumber remaining
    if (sourceCategoryId !== targetCategoryId) {
      const sourceList = sourceCategoryId === null
        ? uncategorizedChannels.filter((c) => c.id !== activeChannelId)
        : (channelsByCategory.get(sourceCategoryId) ?? []).filter((c) => c.id !== activeChannelId);
      if (sourceCategoryId !== null) {
        sourceList.forEach((c, i) => {
          nextAssignments.push({ channelId: c.id, categoryId: sourceCategoryId, sortOrder: i });
        });
      }
      // (uncategorized source: no rows to emit)
    }

    // Target bucket
    if (targetCategoryId !== null) {
      targetList.forEach((c, i) => {
        nextAssignments.push({ channelId: c.id, categoryId: targetCategoryId, sortOrder: i });
      });
    }
    // Uncategorized target: explicit null entry so backend removes any prior assignment
    if (targetCategoryId === null) {
      nextAssignments.push({ channelId: activeChannelId, categoryId: null, sortOrder: 0 });
    }

    // All other categories — keep their assignments untouched
    for (const cat of categories) {
      if (cat.id === sourceCategoryId || cat.id === targetCategoryId) continue;
      const list = channelsByCategory.get(cat.id) ?? [];
      list.forEach((c, i) => {
        nextAssignments.push({ channelId: c.id, categoryId: cat.id, sortOrder: i });
      });
    }

    void syncLayout(
      categories.map((c, i) => ({ id: c.id, sortOrder: i })),
      nextAssignments,
    );
  }, [categories, assignments, uncategorizedChannels, channelsByCategory, filteredChannels, syncLayout]);

  // ── Category CRUD UI ──
  const handleCreateCategory = async () => {
    const name = window.prompt("Name der neuen Kategorie:");
    if (!name || !name.trim()) return;
    await createCategory(name.trim());
  };

  const handleStartRename = (cat: SidebarCategory) => {
    setRenamingCategoryId(cat.id);
    setRenameDraft(cat.name);
    setMenuForCategory(null);
  };

  const handleCommitRename = async () => {
    if (!renamingCategoryId) return;
    const name = renameDraft.trim();
    if (name) await renameCategory(renamingCategoryId, name);
    setRenamingCategoryId(null);
  };

  const handleCancelRename = () => {
    setRenamingCategoryId(null);
  };

  const handleDeleteCategory = async (id: string) => {
    if (!window.confirm("Kategorie löschen? Die Chats bleiben erhalten (landen in 'Ohne Kategorie').")) {
      setMenuForCategory(null);
      return;
    }
    await deleteCategory(id);
    setMenuForCategory(null);
  };

  return (
    <aside className={`${styles.sidebar} ${isCollapsed ? styles.collapsed : ""} sidebar`}>
      {showNewChat && (
        <NewChatDialog
          onClose={() => setShowNewChat(false)}
          onCreated={(channelId) => setActiveChannel(channelId)}
        />
      )}

      {/* Header */}
      <div className={styles.header}>
        {!isCollapsed && (
          <h2 className={styles.brand}>
            <img src="/logo.png" alt="KollegePlus" className={styles.logo} />
            KollegePlus
          </h2>
        )}
        <div className={styles.headerActions}>
          <button
            className={styles.settingsBtn}
            aria-label={isCollapsed ? "Sidebar ausklappen" : "Sidebar einklappen"}
            title={isCollapsed ? "Sidebar ausklappen" : "Sidebar einklappen"}
            onClick={() => setIsCollapsed((v) => !v)}
          >
            {isCollapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
          </button>
          {!isCollapsed && (
            <>
              <button className={styles.settingsBtn} aria-label="Neuer Chat" onClick={() => setShowNewChat(true)}>
                <PenSquare size={18} />
              </button>
              {isAdmin && onAdmin && (
                <button className={styles.settingsBtn} aria-label="Admin-Panel" onClick={onAdmin} title="Admin-Panel">
                  <ShieldCheck size={18} />
                </button>
              )}
              <button className={styles.settingsBtn} aria-label="Einstellungen" onClick={onSettings}>
                <Settings size={18} />
              </button>
            </>
          )}
        </div>
      </div>

      {/* Search */}
      {!isCollapsed && (
        <div className={styles.search}>
          <input
            type="search"
            placeholder="Chats durchsuchen..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={styles.searchInput}
          />
        </div>
      )}

      {/* Channel List */}
      <nav className={styles.channelList} aria-label="Chats">
        {isLoadingChannels ? (
          <div className={styles.skeleton}>
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className={styles.skeletonItem}>
                <div className={styles.skeletonAvatar} />
                {!isCollapsed && (
                  <div className={styles.skeletonLines}>
                    <div className={styles.skeletonLine} />
                    <div className={styles.skeletonLine} />
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : filteredChannels.length === 0 ? (
          !isCollapsed && (
            <div className={styles.empty}>
              {searchQuery ? "Keine Ergebnisse" : "Noch keine Chats"}
            </div>
          )
        ) : isCollapsed ? (
          // Collapsed: category-grouped icons. No drag, but each category is
          // separated by a thin divider that carries a red dot when the category
          // holds unread chats (the per-chat badges are otherwise hidden here).
          <>
            {uncategorizedChannels.map((channel) => (
              <ChannelItem
                key={channel.id}
                channel={channel}
                isActive={channel.id === activeChannelId}
                onClick={() => setActiveChannel(channel.id)}
                currentUserId={user.id}
                onlineUsers={onlineUsers}
                isCollapsed
              />
            ))}
            {categories.map((cat) => {
              const catChannels = channelsByCategory.get(cat.id) ?? [];
              if (catChannels.length === 0) return null;
              const catUnread = catChannels.some((c) => (c.unreadCount ?? 0) > 0);
              return (
                <Fragment key={cat.id}>
                  <div
                    className={styles.categoryDividerCollapsed}
                    title={catUnread ? `${cat.name} — ungelesen` : cat.name}
                  >
                    {catUnread && <span className={styles.categoryDividerDot} />}
                  </div>
                  {catChannels.map((channel) => (
                    <ChannelItem
                      key={channel.id}
                      channel={channel}
                      isActive={channel.id === activeChannelId}
                      onClick={() => setActiveChannel(channel.id)}
                      currentUserId={user.id}
                      onlineUsers={onlineUsers}
                      isCollapsed
                    />
                  ))}
                </Fragment>
              );
            })}
          </>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            {/* Uncategorized section (always at top, no header) */}
            <Bucket id={UNCATEGORIZED_ID}>
              <SortableContext items={uncategorizedChannels.map((c) => c.id)} strategy={verticalListSortingStrategy}>
                {uncategorizedChannels.map((channel) => (
                  <SortableChannelItem
                    key={channel.id}
                    channel={channel}
                    isActive={channel.id === activeChannelId}
                    onClick={() => setActiveChannel(channel.id)}
                    currentUserId={user.id}
                    onlineUsers={onlineUsers}
                  />
                ))}
              </SortableContext>
            </Bucket>

            {/* Categories — headers are drag-sortable to reorder */}
            <SortableContext
              items={categories.map((c) => CATEGORY_DRAG_PREFIX + c.id)}
              strategy={verticalListSortingStrategy}
            >
              {categories.map((cat) => (
                <SortableCategorySection
                  key={cat.id}
                  cat={cat}
                  channels={channelsByCategory.get(cat.id) ?? []}
                  isRenaming={renamingCategoryId === cat.id}
                  renameDraft={renameDraft}
                  activeChannelId={activeChannelId}
                  currentUserId={user.id}
                  onlineUsers={onlineUsers}
                  onToggleExpanded={() => toggleExpanded(cat.id)}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    setMenuForCategory({ id: cat.id, x: e.clientX, y: e.clientY });
                  }}
                  onMenuOpen={(x, y) => setMenuForCategory({ id: cat.id, x, y })}
                  onStartRename={() => handleStartRename(cat)}
                  onRenameChange={setRenameDraft}
                  onRenameCommit={handleCommitRename}
                  onRenameCancel={handleCancelRename}
                  onChannelClick={setActiveChannel}
                />
              ))}
            </SortableContext>

            {/* + Kategorie */}
            <button className={styles.newCategoryBtn} onClick={handleCreateCategory}>
              <Plus size={14} /> Neue Kategorie
            </button>
          </DndContext>
        )}
      </nav>

      {/* Context Menu */}
      {menuForCategory && !isCollapsed && (
        <div
          className={styles.contextMenu}
          style={{ left: menuForCategory.x, top: menuForCategory.y }}
          onClick={(e) => e.stopPropagation()}
        >
          <button onClick={() => {
            const cat = categories.find((c) => c.id === menuForCategory.id);
            if (cat) handleStartRename(cat);
          }}>Umbenennen</button>
          <button onClick={() => handleDeleteCategory(menuForCategory.id)} className={styles.dangerItem}>
            Löschen
          </button>
        </div>
      )}

      {/* Verbrauch der Organisation (KOL-94) — über dem Benutzernamen.
          Erst ab 90 % Auslastung, für alle Rollen gleich (KOL-132): Die
          Zeile trägt die Trennlinie zum Footer, deshalb muss die Schwelle
          hier greifen und nicht erst in der Komponente. */}
      {orgUsage && zeigeVerbrauch(orgUsage.used, orgUsage.quota) && (
        <div className={styles.usageRow}>
          <UsageBar
            used={orgUsage.used}
            quota={orgUsage.quota}
            collapsed={isCollapsed}
            orgName={orgUsage.orgName}
            foreign={orgUsage.foreign}
          />
        </div>
      )}

      {/* User Footer */}
      <div className={styles.footer}>
        <Avatar name={user.displayName} src={user.avatarUrl} size="sm" online />
        {!isCollapsed && (
          <>
            <div className={styles.userInfo}>
              <span className={styles.userName}>{user.displayName}</span>
              <span className={styles.deviceId}>
                {(() => { try { return localStorage.getItem("kollegeplus:deviceId")?.slice(-6) ?? "…"; } catch { return "…"; } })()}
              </span>
            </div>
            <button onClick={onLogout} className={styles.logoutBtn} aria-label="Abmelden">
              <LogOut size={18} />
            </button>
          </>
        )}
      </div>
    </aside>
  );
}

// ============================================================
// Droppable Bucket wrapper
// ============================================================
function Bucket({ id, children }: { id: string; children: React.ReactNode }) {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <div ref={setNodeRef} className={`${styles.bucket} ${isOver ? styles.bucketOver : ""}`}>
      {children}
    </div>
  );
}

// ============================================================
// Sortable Category Section (draggable header + channel bucket)
// ============================================================
function SortableCategorySection({
  cat, channels, isRenaming, renameDraft, activeChannelId, currentUserId, onlineUsers,
  onToggleExpanded, onContextMenu, onMenuOpen, onStartRename, onRenameChange, onRenameCommit,
  onRenameCancel, onChannelClick,
}: {
  cat: SidebarCategory;
  channels: Channel[];
  isRenaming: boolean;
  renameDraft: string;
  activeChannelId: string | null;
  currentUserId: string;
  onlineUsers: Set<string>;
  onToggleExpanded: () => void;
  onContextMenu: (e: React.MouseEvent) => void;
  onMenuOpen: (x: number, y: number) => void;
  onStartRename: () => void;
  onRenameChange: (v: string) => void;
  onRenameCommit: () => void;
  onRenameCancel: () => void;
  onChannelClick: (id: string) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: CATEGORY_DRAG_PREFIX + cat.id });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : undefined,
  };

  // Unread highlight: sum of unread across the (possibly collapsed) category.
  const unreadTotal = channels.reduce((n, c) => n + (c.unreadCount ?? 0), 0);
  const hasUnread = unreadTotal > 0;

  return (
    <div ref={setNodeRef} style={style} className={styles.categorySection}>
      <div className={styles.categoryHeader} onContextMenu={onContextMenu}>
        {/* Drag handle — only this carries the sortable listeners, so the toggle,
            rename input and menu button stay fully interactive. */}
        <button
          className={styles.categoryDragHandle}
          aria-label="Kategorie verschieben"
          title="Zum Umsortieren ziehen"
          {...attributes}
          {...listeners}
        >
          <GripVertical size={14} />
        </button>
        <button
          className={styles.categoryToggle}
          onClick={onToggleExpanded}
          aria-label={cat.isExpanded ? "Einklappen" : "Ausklappen"}
        >
          {cat.isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </button>
        {isRenaming ? (
          <input
            autoFocus
            className={styles.categoryRenameInput}
            value={renameDraft}
            onChange={(e) => onRenameChange(e.target.value)}
            onBlur={onRenameCommit}
            onKeyDown={(e) => {
              if (e.key === "Enter") onRenameCommit();
              else if (e.key === "Escape") onRenameCancel();
            }}
          />
        ) : (
          <span className={styles.categoryName} onDoubleClick={onStartRename}>
            {cat.name}
          </span>
        )}
        <span
          className={`${styles.categoryCount} ${hasUnread ? styles.categoryCountUnread : ""}`}
          title={hasUnread ? `${unreadTotal} ungelesen` : undefined}
        >
          {channels.length}
        </span>
        <button
          className={styles.categoryMenuBtn}
          aria-label="Kategorie-Menü"
          onClick={(e) => {
            e.stopPropagation();
            const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
            onMenuOpen(rect.left, rect.bottom);
          }}
        >
          <MoreVertical size={14} />
        </button>
      </div>
      {cat.isExpanded && (
        <Bucket id={cat.id}>
          <SortableContext items={channels.map((c) => c.id)} strategy={verticalListSortingStrategy}>
            {channels.map((channel) => (
              <SortableChannelItem
                key={channel.id}
                channel={channel}
                isActive={channel.id === activeChannelId}
                onClick={() => onChannelClick(channel.id)}
                currentUserId={currentUserId}
                onlineUsers={onlineUsers}
              />
            ))}
            {channels.length === 0 && (
              <div className={styles.emptyCategory}>Chats hierher ziehen</div>
            )}
          </SortableContext>
        </Bucket>
      )}
    </div>
  );
}

// ============================================================
// Sortable Channel Item
// ============================================================
function SortableChannelItem(props: {
  channel: Channel;
  isActive: boolean;
  onClick: () => void;
  currentUserId: string;
  onlineUsers: Set<string>;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: props.channel.id });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : undefined,
  };
  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <ChannelItem {...props} isCollapsed={false} />
    </div>
  );
}

// ============================================================
// Channel Item
// ============================================================
const ChannelItem = memo(function ChannelItem({
  channel, isActive, onClick, currentUserId, onlineUsers, isCollapsed,
}: {
  channel: Channel;
  isActive: boolean;
  onClick: () => void;
  currentUserId: string;
  onlineUsers: Set<string>;
  isCollapsed: boolean;
}) {
  const hasUnread = (channel.unreadCount ?? 0) > 0;

  const dmPartner = channel.type === "dm"
    ? channel.members?.find((m) => m.userId !== currentUserId)
    : null;
  const name = (channel.type === "dm" && dmPartner?.displayName)
    ? dmPartner.displayName
    : channel.name ?? "Direktnachricht";
  const isOnline = dmPartner ? onlineUsers.has(dmPartner.userId) : undefined;

  return (
    <button
      className={`${styles.channelItem} ${isActive ? styles.active : ""} ${isCollapsed ? styles.channelItemCollapsed : ""}`}
      onClick={onClick}
      title={isCollapsed ? name : undefined}
    >
      <div className={styles.avatarWrap}>
        <Avatar name={name} size="sm" online={isOnline} />
        {isCollapsed && hasUnread && <span className={styles.unreadDot} aria-label={`${channel.unreadCount} ungelesen`} />}
      </div>
      {!isCollapsed && (
        <>
          <div className={styles.channelInfo}>
            <span className={`${styles.channelName} ${hasUnread ? styles.unread : ""}`}>
              {channel.type === "dm"
                ? <User size={13} className={styles.channelTypeIcon} />
                : <Users size={13} className={styles.channelTypeIcon} />
              }
              {name}
            </span>
            {channel.lastMessage && (
              <span className={styles.lastMessage}>
                {safePreviewText(channel.lastMessage, "Neue Nachricht")}
              </span>
            )}
          </div>
          {hasUnread && <span className={styles.badge}>{channel.unreadCount}</span>}
        </>
      )}
    </button>
  );
});
